import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

interface SystemSettings {
  announcementCheckInterval: number;
  adCheckInterval: number;
  playlistCheckInterval: number;
  debugModeEnabled: boolean;
}

interface SystemSettingsContextType extends SystemSettings {
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const defaultSettings: SystemSettings = {
  announcementCheckInterval: 30,
  adCheckInterval: 60,
  playlistCheckInterval: 300,
  debugModeEnabled: false,
};

const SystemSettingsContext = createContext<SystemSettingsContextType>({
  ...defaultSettings,
  loading: true,
  error: null,
  refetch: async () => {},
});

export const useSystemSettings = () => {
  const context = useContext(SystemSettingsContext);
  if (!context) {
    throw new Error('useSystemSettings must be used within SystemSettingsProvider');
  }
  return context;
};

export const SystemSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<SystemSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('system_settings')
        .select('*');

      if (fetchError) throw fetchError;

      if (data) {
        const newSettings: SystemSettings = { ...defaultSettings };

        data.forEach((setting: any) => {
          switch (setting.setting_key) {
            case 'announcement_check_interval':
              newSettings.announcementCheckInterval = setting.setting_value === '' ? 0 : parseInt(setting.setting_value) || 30;
              break;
            case 'ad_check_interval':
              newSettings.adCheckInterval = setting.setting_value === '' ? 0 : parseInt(setting.setting_value) || 60;
              break;
            case 'playlist_check_interval':
              newSettings.playlistCheckInterval = setting.setting_value === '' ? 0 : parseInt(setting.setting_value) || 300;
              break;
            case 'debug_mode_enabled':
              newSettings.debugModeEnabled = setting.setting_value === 'true';
              break;
          }
        });

        setSettings(newSettings);
      }
    } catch (err) {
      console.error('Error loading system settings:', err);
      setError('Failed to load system settings');
      setSettings(defaultSettings);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();

    const channel = supabase
      .channel('system_settings_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'system_settings',
        },
        (payload) => {
          loadSettings();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return (
    <SystemSettingsContext.Provider
      value={{
        ...settings,
        loading,
        error,
        refetch: loadSettings,
      }}
    >
      {children}
    </SystemSettingsContext.Provider>
  );
};
